import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Button,
  Box,
  DialogTitle,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Chip,
  Paper,
  Fade
} from '@mui/material';
import axios from 'src/utils/axios';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RefreshIcon from '@mui/icons-material/Refresh';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import SearchIcon from '@mui/icons-material/Search';
import BagliUrunAutocomplete from './BagliUrunAutocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Alert from '@mui/material/Alert';
import { havcCancelButton, havcNeutralButton, havcPrimaryButton } from './havcTheme';
import Autocomplete from '@mui/material/Autocomplete';
import ReceiptCollapsibleTable from './ReceiptCollapsibleTable';
import CheckIcon from '@mui/icons-material/Check';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { getUser } from 'src/utils/Common';

const SessionUser = getUser();

// Modern stil tanımları
const styles = {
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '16px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    },
  },
  dialogContent: {
    padding: '24px',
    backgroundColor: '#f8fafc',
  },
  dialogActions: {
    padding: '16px 24px',
    backgroundColor: '#ffffff',
    borderTop: '1px solid #e2e8f0',
    gap: '12px',
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: '#ffffff',
      '&:hover': {
        backgroundColor: '#f8fafc',
      },
      '&.Mui-focused': {
        backgroundColor: '#ffffff',
        boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.1)',
      },
    },
  },
  actionButton: {
    borderRadius: '4px',
    padding: '6px 16px',
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '0.875rem',
    backgroundColor: '#424242 !important',
    color: '#ed6c02 !important',
    border: 'none !important',
    '&:hover': {
      backgroundColor: '#616161 !important',
    },
    '&.Mui-disabled': {
      backgroundColor: '#424242 !important',
      color: '#ed6c02 !important',
      opacity: 0.7,
    },
  },
  saveButton: {},
  closeButton: {},
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '64px',
    gap: '16px',
  },
  alertSuccess: {
    borderRadius: '12px',
    margin: '0 24px 16px',
  },
  alertError: {
    borderRadius: '12px',
    margin: '0 24px 16px',
  },
  reminderDialog: {
    '& .MuiDialog-paper': {
      borderRadius: '16px',
      overflow: 'hidden',
    },
  },
  reminderTitle: {
    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    color: '#ffffff',
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  reminderContent: {
    padding: '32px 24px',
    textAlign: 'center',
  },
  reminderActions: {
    padding: '16px 24px 24px',
    backgroundColor: '#f8fafc',
    borderTop: '1px solid #e2e8f0',
    gap: '12px',
    justifyContent: 'center',
  },
  confirmButton: {
    borderRadius: '12px',
    padding: '12px 32px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.9rem',
    backgroundColor: 'transparent',
    border: '2px solid #f59e0b',
    color: '#1f2937',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: 'rgba(245, 158, 11, 0.08)',
      border: '2px solid #d97706',
      boxShadow: 'none',
    },
  },
  cancelButton: {
    borderRadius: '12px',
    padding: '12px 32px',
    textTransform: 'none',
    fontWeight: 600,
    borderColor: '#e2e8f0',
    color: '#64748b',
    '&:hover': {
      backgroundColor: '#f1f5f9',
      borderColor: '#cbd5e1',
    },
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      minHeight: '40px',
      height: 'auto',
      '& .MuiOutlinedInput-input': {
        padding: '8px 14px',
        height: 'auto',
        lineHeight: '1.5',
        display: 'flex',
        alignItems: 'center',
      },
    },
    '& .MuiInputLabel-root': {
      lineHeight: '1.2',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
      },
      '&:not(.MuiInputLabel-shrink)': {
        transform: 'translate(14px, 10px) scale(1)',
      },
    },
    '& .MuiAutocomplete-inputRoot': {
      padding: '0 9px !important',
      '& .MuiAutocomplete-input': {
        padding: '8px 4px !important',
      },
    },
  },
  bagliUrunColumn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '4px 0',
  },
  technicianColumn: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '4px 0',
  },
};

// İptal edilen faturalar için log
function getCancelledDiffs(cancelleds) {
  return cancelleds.map(item => ({
    oldValue: item.FisNo,
    newValue: null,
    key: 'İptal Edilen Fatura',
    id: null,
    fisNo: item.FisNo
  }));
}

// Barcodes için diff array'i (yeni eklenenleri bulur)
function getBarcodeDiffs(initialBarcodes, barcodes) {
  const diffs = [];
  const initialById = Object.fromEntries(initialBarcodes.map(x => [x.Id, x]));
  const currentById = Object.fromEntries(barcodes.map(x => [x.Id, x]));

  // Find new and changed barcodes
  for (const barcode of barcodes) {
    const initial = initialById[barcode.Id];
    if (!initial) {
      // New barcode
      diffs.push({
        oldValue: null,
        newValue: barcode.Barcode,
        key: 'Barkod',
        id: null, //initial.BindedSystemId,
        fisNo: barcode.FisNo
      });
    } else if (initial.Barcode !== barcode.Barcode) {
      // Changed barcode
      diffs.push({
        oldValue: initial.Barcode,
        newValue: barcode.Barcode,
        key: 'Barkod',
        id: null, //initial.BindedSystemId,
        fisNo: barcode.FisNo
      });
    }
  }

  // removed barcode
  for (const initial of initialBarcodes) {
    if (!currentById[initial.Id]) {
      diffs.push({
        oldValue: initial.Barcode,
        newValue: null,
        key: 'Barkod',
        id: null, //initial.BindedSystemId,
        fisNo: initial.FisNo
      });
    }
  }

  return diffs;
}

function Receipts({
  open,
  setOpen,
  selectedRow,
  setSelectedRow,
  sorgula,
  fislerYeniEkraniAc,
  onUpdateSuccess
}) {
  const [data, setData] = useState([]);
  const [cancelledReceipts, setCancelledReceipts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [bagliUrunData, setBagliUrunData] = useState([]);
  const [loading, setLoading] = React.useState(false);
  const [errorsArray, setErrorsArray] = useState([]);
  const [errors, setErrors] = useState([]);
  const [showAlert, setShowAlert] = useState(false);
  const [yuklemeHatasi, setYuklemeHatasi] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [reminder, setReminder] = useState(false);
  const [lgmvWarning, setLgmvWarning] = useState(false);
  const [activeRowFisNo, setActiveRowFisNo] = useState(null);
  const [errorFisNo, setErrorFisNo] = useState(null);
  const isMobile = false; //useMediaQuery('(max-width:600px)');
  const [initialBarcodes, setInitialBarcodes] = useState([]);
  const [initialBindings, setInitialBindings] = useState([]);

  // Dış Ünite kontrolü - SystemName alanından tanıma
  const isDısUnite = (receipt) => {
    const name = (receipt.SystemName || '').toLowerCase();
    return name.includes('dış ünite') || name.includes('dış unite') || name.includes('dis ünite') || name.includes('dis unite');
  };

  // LGMV'si olmayan sistem ID'lerini bul (Level 1, Type 0 parent'lar)
  const systemsWithoutLgmv = (() => {
    if (!data?.Receipts?.length) return new Set();
    return new Set(
      data.Receipts
        .filter(x => x.Level === 1 && x.Type === 0 && (!x.lgmvFieldUpload || x.lgmvFieldUpload <= 0))
        .map(x => x.DetailId)
    );
  })();

  // Dış ünite barkodu kontrolü: hem React state hem DOM input değerlerini kontrol eder
  // (uncontrolled input blur olmadan butona tıklanırsa state güncel olmayabilir)
  const checkOutdoorBarcodeInNonLgmvSystem = () => {
    if (!data?.Receipts?.length || systemsWithoutLgmv.size === 0) return false;
    return data.Receipts.some(x => {
      if (x.FisNo <= 0 || !isDısUnite(x) || !systemsWithoutLgmv.has(x.BindedSystemId)) return false;
      // State'deki değer
      if (x.Barcode && x.Barcode.length > 0) return true;
      // DOM'daki değer (blur henüz tetiklenmemiş olabilir)
      const input = document.querySelector(`input#Barcode-${x.Id}`);
      return input && input.value && input.value.length > 0;
    });
  };

  useEffect(() => {
    if (data?.Receipts?.length > 0) {
      setInitialBarcodes(data.Receipts.filter(x => x.Barcode));
    }
  }, [data.Receipts]);

  useEffect(() => {
    if (bagliUrunData?.length > 0) {
      setInitialBindings(bagliUrunData);
    }
  }, [bagliUrunData]);

  // useEffect(() => {
  //   if (columns.length > 0) {
  //     setSelectedColumns(columns.map(col => col.headerName));
  //   }
  // }, [columns]);

  // useEffect(() => {
  //   if (columns.length > 0) {
  //     setSelectedColumns(columns.map(col => col.headerName));
  //   }
  // }, [isMobile]);

  useEffect(() => {
    if (errorsArray.length > 0) {
      validateForm();
    }
  }, [errorsArray]);

  const validateForm = () => {
    let tempErrors = { ...errors };

    data.Receipts.forEach((item, index) => {
      // Barcode validation
      if (errorsArray.some(x => x.FisNo === item.FisNo)) {
        tempErrors[`Barcode${item.FisNo}`] = errorsArray.find(
          x => x.FisNo === item.FisNo
        ).Msg;
      } else {
        tempErrors[`Barcode${item.FisNo}`] = '';
      }
    });

    setErrors(tempErrors);
  };
  const validateFormBeforeSave = () => {
    let isValid = true;
    let tempErrors = { ...errors };

    data.Receipts.forEach((item, index) => {
      // LGMV'siz sistemdeki Dış Ünite gönderilmeyecek, validasyon atla
      const willBeExcluded = isDısUnite(item) && systemsWithoutLgmv.has(item.BindedSystemId);

      // TechnicianId validation
      if (!willBeExcluded && item.Barcode.length > 0 && item.TechnicianId === 0) {
        tempErrors[`TechnicianId${item.FisNo}`] = 'Teknisyen seçimi yapılmalı!';
        isValid = false;
      } else {
        tempErrors[`TechnicianId${item.FisNo}`] = '';
      }
    });

    setErrors(tempErrors);
    return isValid;
  };

  const handleChange = (newValue, rowId) => {
    if (newValue) {
      const updatedSystem = data.Receipts.find(row => row.Id === rowId)
        ?.BindedSystemId;

      setData(prevData => ({
        ...prevData,
        Receipts: prevData.Receipts.map(row =>
          row.BindedSystemId === updatedSystem && row.TechnicianId === 0
            ? { ...row, TechnicianId: newValue?.Id ? newValue?.Id : 0 }
            : row
        )
      }));
    }

    setData(prevData => ({
      ...prevData,
      Receipts: prevData?.Receipts?.map(row =>
        row.Id === rowId
          ? { ...row, TechnicianId: newValue?.Id ? newValue?.Id : 0 }
          : row
      )
    }));
  };

  const handleBarcodeBlur = (event, rowId) => {
    const newValue = event.target.value;

    setData(prevData => ({
      ...prevData,
      Receipts: prevData?.Receipts?.map(row =>
        row.Id === rowId ? { ...row, Barcode: newValue } : row
      )
    }));
  };

  const handleRenewClick = (barcode, rowId, FisNo, TechnicianId) => {
    setErrorFisNo(null);
    //setActiveRowFisNo(FisNo);
    //callLineBarcodeApi(barcode, rowId, FisNo, TechnicianId);
  };

  const handleBarcodeChange = (event, rowId, FisNo, TechnicianId) => {
    const barcode = event.target.value;

    if (barcode.length > 21) {
      //setActiveRowFisNo(FisNo);
      //callLineBarcodeApi(barcode, rowId, FisNo, TechnicianId);
      const nextRowId = getNextRowId(rowId);
      if (nextRowId) {
        const nextField = document.querySelector(`input#Barcode-${nextRowId}`);
        if (nextField) {
          nextField.focus();
        }
      }
    }
  };

  const callLineBarcodeApi = (barcode, rowId, FisNo, TechnicianId) => {
    setErrors([]);
    axios
      .post(
        `SetHavcSingleLineBarcode/${selectedRow.ProjectID}/${FisNo}/${barcode}/${TechnicianId}`
      )
      .then(response => {
        if (response.data.includes('Error:')) {
          setErrors({
            [`Barcode${FisNo}`]: response.data.split('Error:')[1]
          });
          setActiveRowFisNo(null);
          setErrorFisNo(FisNo);
        } else {
          const dataCopy = [...data.Receipts];
          const technicianMap = new Map(
            dataCopy.map(receipt => [receipt.Id, receipt.TechnicianId])
          );
          fetchHavcProjectReceipts(selectedRow.ProjectID).then(() => {
            setData(prevData => ({
              ...prevData,
              Receipts: prevData.Receipts.map(receipt => ({
                ...receipt,
                TechnicianId:
                  receipt.FisNo > 0
                    ? technicianMap.get(receipt.Id) || receipt.TechnicianId
                    : receipt.TechnicianId
              }))
            }));
          });
          const nextRowId = getNextRowId(rowId);
          if (nextRowId) {
            const nextField = document.querySelector(
              `input#Barcode-${nextRowId}`
            );
            if (nextField) {
              nextField.focus();
            }
          }
          setActiveRowFisNo(null);
        }
      })
      .catch(error => {
        setActiveRowFisNo(null);
      })
      .finally(() => {
        setActiveRowFisNo(null);
      });
  };

  const getNextRowId = currentRowId => {
    return data?.Receipts.find(
      row => row.Id > currentRowId && row.FisNo > 0 && row.Barcode === ''
    ).Id;
  };

  const save = () => {
    if (
      !validateFormBeforeSave() ||
      Object.values(errors).some(value => value.trim() !== '')
    ) {
      setShowAlert(true);
      return;
    }
    if (!loading) {
      setLoading(true);
    }
    // Gönderilebilir barkodları filtrele (DOM'dan güncel değerleri al)
    const barcodes = data.Receipts
      .map(x => {
        const input = document.querySelector(`input#Barcode-${x.Id}`);
        const currentBarcode = (input ? input.value : x.Barcode) || '';
        return { ...x, Barcode: currentBarcode };
      })
      .filter(x => {
        if (!x.Barcode || x.Barcode.length === 0) return false;
        // LGMV'siz sistemdeki Dış Üniteleri hariç tut
        if (isDısUnite(x) && systemsWithoutLgmv.has(x.BindedSystemId)) return false;
        // Diğer tüm ekipmanlar: barkod girilmişse gönderilebilir
        return true;
      });
    const bindings = bagliUrunData;
    const cancelleds = cancelledReceipts;

    const body = {
      barcodes: barcodes,
      bindings: bindings,
      cancelledReceipts: cancelleds
    };
    const config = {
      headers: { 'Content-Type': 'application/json' }
    };
    //log
      const cancelledDiffs = getCancelledDiffs(cancelledReceipts);
      const barcodeDiffs = getBarcodeDiffs(initialBarcodes, data.Receipts.filter(x => x.Barcode));
      const changes = cancelledDiffs.concat(barcodeDiffs);
      if(Array.isArray(changes) && changes.length > 0){
        const changesWithProjectId = changes.map(item => ({
          ...item,
          ProjectID: selectedRow.ProjectID
        }));
        axios.post('SetHavcProjectLog', changesWithProjectId, config);
      }
    //log
    
    axios
      .post('SetHavcBarcodes', body, config)
      .then(response => {
        if (response.data.includes('Error:')) {
          const startIndex = response.data.indexOf('Error:') + 'Error:'.length;
          const endIndex = response.data.length;
          const extractedText = response.data
            .substring(startIndex, endIndex)
            .trim();
          const resultArray = JSON.parse(extractedText);
          setErrorsArray(resultArray);
          setLoading(false);
          setShowAlert(true);
        } else {
          axios
            .post(`callHavcDogrula/${selectedRow.ProjectID}`, config)
            .then(secondResponse => {})
            .catch(secondError => {});
          setData([]);
          setSelectedRow([]);
          setBagliUrunData([]);
          setErrors([]);
          setErrorsArray([]);
          setSearchQuery('');
          setLoading(false);
          setOpen(false);
          setShowAlert(false);
          onUpdateSuccess(selectedRow.ProjectID);
        }
      })
      .catch(error => {
        setLoading(false);
      });
  };

  const handleDialogClose = () => {
    setData([]);
    setBagliUrunData([]);
    setSelectedRow([]);
    setErrors([]);
    setErrorsArray([]);
    setSearchQuery('');
    setLoading(false);
    setShowAlert(false);
    setOpen(false);
  };

  const getIconForLevel = level => {
    switch (level) {
      case 0:
        return <AccountTreeIcon />;
      case 1:
        return <AcUnitIcon />;
      case 2:
        return <ReceiptLongIcon />;
      case 3:
        return <InsertDriveFileIcon />;
      default:
        return null;
    }
  };

  const renderCell = params => {
    if (params.field === 'ReceiptStatusExplanation' && params.row.FisNo > 1) {
      return (
        <Chip
          label={params.value}
          size="small"
          sx={params.row.StatusCompleted ? {
            backgroundColor: '#dcfce7',
            color: '#166534',
            fontWeight: 600,
            fontSize: '0.75rem',
          } : {
            backgroundColor: '#fef2f2',
            color: '#dc2626',
            fontWeight: 600,
            fontSize: '0.75rem',
          }}
          title={params.row.DogrulamaNotes}
        />
      );
    } else if (params.field === 'Barcode' && params.row.FisNo > 0) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', overflow: 'visible', py: 0.5 }}>
          <TextField
            id={`Barcode-${params.row.Id}`}
            name="Barcode"
            label="Barkod"
            placeholder="Ürün Barkodu"
            variant="outlined"
            size="small"
            sx={styles.textField}
            error={Boolean(errors[`Barcode${params.row.FisNo}`])}
            helperText={errors[`Barcode${params.row.FisNo}`]}
            fullWidth
            defaultValue={params.value || ''}
            disabled={params.row.BarcodeControl === 1}
            onBlur={event => handleBarcodeBlur(event, params.row.Id)}
            onChange={event =>
              handleBarcodeChange(
                event,
                params.row.Id,
                params.row.FisNo,
                data.Receipts.find(x => x.Id === params.row.Id).TechnicianId
              )
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  {errorFisNo === params.row.FisNo ? (
                    <IconButton
                      size="small"
                      sx={{ color: '#3b82f6' }}
                      onClick={event =>
                        handleRenewClick(
                          params.row.Barcode,
                          params.row.Id,
                          params.row.FisNo,
                          data.Receipts.find(x => x.Id === params.row.Id)
                            .TechnicianId
                        )
                      }
                    >
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    activeRowFisNo === params.row.FisNo && (
                      <CircularProgress size={18} />
                    )
                  )}
                </InputAdornment>
              )
            }}
          />
          {params.row.Barcode > 0 &&
            Array.isArray(errors) &&
            !errors.some(item =>
              item.hasOwnProperty(`Barcode${params.row.FisNo}`)
            ) &&
            errorFisNo !== params.row.FisNo &&
            activeRowFisNo !== params.row.FisNo && (
              <CheckCircleIcon sx={{ color: '#16a34a', fontSize: '22px' }} />
            )}
        </Box>
      );
    } else if (
      params.field === 'BagliUrun' &&
      (params.row.Type === 16 || params.row.Type === 17)
    ) {
      return (
        <Box sx={styles.bagliUrunColumn}>
          <BagliUrunAutocomplete
            data={data}
            type={params.row.Type}
            fisNo={params.row.FisNo}
            bagliUrunData={bagliUrunData}
            setBagliUrunData={setBagliUrunData}
          />
        </Box>
      );
    } else if (params.field === 'TechnicianId' && params.row.FisNo > 0) {
      const FisNo = params.row.FisNo;
      return (
        <Box sx={styles.technicianColumn}>
          <Autocomplete
            id="TechnicianId"
            name="TechnicianId"
            fullWidth
            size="small"
            disabled={params.row.TechnicianControl === 1}
            options={data.Technicians}
            getOptionLabel={teknisyen => (teknisyen.Dsc ? teknisyen.Dsc : '')}
            value={
              data?.Technicians?.find(
                x =>
                  x.Id ===
                  data.Receipts.find(x => x.Id === params.row.Id).TechnicianId
              ) || null
            }
            onChange={(event, newValue) =>
              handleChange(newValue, params.row.Id)
            }
            renderInput={params => (
              <TextField
                {...params}
                label="Sorumlu Teknisyen"
                variant="outlined"
                size="small"
                error={Boolean(errors[`TechnicianId${FisNo}`])}
                helperText={errors[`TechnicianId${FisNo}`]}
                sx={styles.textField}
              />
            )}
          />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          paddingLeft:
            params.row.Level === 2 && params.field === 'SystemName'
              ? `${params.row.Level * 10}px`
              : 0
        }}
      >
        {params.field === 'SystemName' && getIconForLevel(params.row.Level)}
        <Typography sx={{ fontSize: '0.875rem' }}>
          {(params.field === 'FisNo' || params.field === 'TechnicianId') &&
          params.value === 0
            ? ''
            : params.value}
        </Typography>
      </Box>
    );
  };

  const columns = [
    {
      field: 'SystemName',
      headerName: 'SistemAdı',
      flex: 0.7,
      sortable: false,
      disableColumnMenu: true,
      renderCell
    },
    {
      field: 'ModelCode',
      headerName: 'ModelKod',
      sortable: false,
      disableColumnMenu: true,
      flex: 0.7,
      renderCell
    },
    {
      field: 'ModelDesc',
      headerName: 'ModelAçıklama',
      sortable: false,
      disableColumnMenu: true,
      flex: 1.5
    },
    {
      field: 'FisNo',
      headerName: 'FişNo',
      sortable: false,
      disableColumnMenu: true,
      flex: 0.6,
      renderCell
    },
    {
      field: 'ReceiptStatusExplanation',
      headerName: 'FişDurumu',
      sortable: false,
      disableColumnMenu: true,
      flex: 1,
      renderCell
    },
    {
      field: 'BagliUrun',
      headerName: 'BağlıÜrünler',
      sortable: false,
      disableColumnMenu: true,
      flex: 2.2,
      renderCell
    },
    {
      field: 'TechnicianId',
      headerName: 'Teknisyen',
      sortable: false,
      disableColumnMenu: true,
      flex: 2,
      renderCell
    },
    {
      field: 'Barcode',
      headerName: 'ÜrünBarkodu',
      flex: 1.8,
      minWidth: 180,
      sortable: false,
      disableColumnMenu: true,
      renderCell
    }
  ];

  const getRowHeight = () => 'auto';

  useEffect(() => {
    fetchHavcProjectReceipts(selectedRow.ProjectID);
  }, [selectedRow]);

  const fetchHavcProjectReceipts = async projectId => {
    setIsLoading(true);
    try {
      const response = await axios.get(`GetHavcProjectReceipts/${projectId}`);
      setData(response.data ? response.data : []);
      setBagliUrunData(response.data.Bindings ? response.data.Bindings : []);
    } catch (error) {
      setYuklemeHatasi('Fiş bilgileri yüklenemedi. Lütfen tekrar deneyin.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
  };

  const filteredReceipts = data?.Receipts?.filter(receipt => {
    return Object.values(receipt).some(value =>
      value
        ?.toString()
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );
  });

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        maxWidth="xl"
        onClose={() => setOpen(false)}
        sx={styles.dialog}
        TransitionComponent={Fade}
        transitionDuration={300}
      >
        <DialogContent sx={styles.dialogContent}>
          {data?.Receipts?.length > 0 ? (
            <>
              {fislerYeniEkraniAc === false ? (
                <Box>
                  <Paper sx={{ padding: '16px', borderRadius: '12px', marginBottom: '16px' }}>
                    <TextField
                      label="Ara"
                      placeholder="Fiş no, model veya sistem adı ile arayın..."
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      sx={styles.searchField}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#94a3b8' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Paper>
                  <Paper sx={{ borderRadius: '12px', overflow: 'hidden' }}>
                    <DataGrid
                      rows={filteredReceipts}
                      columns={columns}
                      disableSelectionOnClick
                      hideFooter
                      getRowId={row => row.Id}
                      autoGroupColumnDef={{
                        field: 'Id'
                      }}
                      autoHeight
                      getRowHeight={getRowHeight}
                      sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                          backgroundColor: '#f8fafc',
                          fontWeight: 600,
                        },
                        '& .MuiDataGrid-row:hover': {
                          backgroundColor: '#f8fafc',
                        },
                        '& .MuiDataGrid-cell': {
                          borderBottom: '1px solid #f1f5f9',
                          overflow: 'visible !important',
                          padding: '4px 8px',
                        },
                      }}
                    />
                  </Paper>
                </Box>
              ) : (
                <ReceiptCollapsibleTable
                  data={data}
                  setData={setData}
                  bagliUrunData={bagliUrunData}
                  setBagliUrunData={setBagliUrunData}
                  errors={errors}
                  setErrors={setErrors}
                  errorsArray={errorsArray}
                  setErrorsArray={setErrorsArray}
                  selectedRow={selectedRow}
                  fetchHavcProjectReceipts={fetchHavcProjectReceipts}
                  cancelledReceipts={cancelledReceipts}
                  setCancelledReceipts={setCancelledReceipts}
                />
              )}
            </>
          ) : (
            <Box sx={styles.loadingContainer}>
              <CircularProgress size={48} thickness={4} sx={{ color: '#1976d2' }} />
              <Typography sx={{ color: '#64748b', fontWeight: 500 }}>
                Fişler yükleniyor...
              </Typography>
            </Box>
          )}
        </DialogContent>

        {yuklemeHatasi && (
          <Alert
            severity="warning"
            sx={styles.alertError}
            onClose={() => setYuklemeHatasi('')}
          >
            {yuklemeHatasi}
          </Alert>
        )}

        {showAlert && (
          <Alert
            severity="error"
            sx={styles.alertError}
            onClose={() => setShowAlert(false)}
          >
            Hatalı girilen barkodlar mevcut!
          </Alert>
        )}

        <DialogActions sx={styles.dialogActions}>
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
            <Button
              variant="contained"
              disabled={loading || !data?.Receipts || data?.Receipts?.length === 0}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={() => {
                const hasPreviouslySaved = data?.Receipts?.filter(x => x.BarcodeControl === 1 && x.Barcode.length > 0).length > 0;
                if (checkOutdoorBarcodeInNonLgmvSystem() || hasPreviouslySaved) {
                  setReminder(true);
                } else {
                  save();
                }
              }}
              sx={havcPrimaryButton}
            >
              {loading ? 'Gönderiliyor...' : 'Merkeze Gönder'}
            </Button>
          </Box>
          <Button
            variant="contained"
            endIcon={<CloseIcon />}
            onClick={handleDialogClose}
            sx={havcNeutralButton}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reminder Dialog */}
      <Dialog
        open={reminder}
        onClose={() => setReminder(false)}
        sx={styles.reminderDialog}
        TransitionComponent={Fade}
        transitionDuration={300}
      >
        <DialogTitle sx={styles.reminderTitle}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Merkeze Gönderim Uyarısı
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ ...styles.reminderContent, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
          <WarningAmberIcon sx={{ fontSize: '64px', color: '#f59e0b', marginBottom: '16px' }} />
          <Typography sx={{ color: '#475569', fontSize: '1rem', lineHeight: 1.6 }}>
            Ürünlerin garantisi başlatılacaktır. Ürün fişlerini garantisinin
            başlatılması için bölge onayına göndermek istediğinize emin
            misiniz?
          </Typography>
        </DialogContent>
        <DialogActions sx={styles.reminderActions}>
          <Button
            variant="outlined"
            startIcon={<CloseIcon />}
            onClick={() => setReminder(false)}
            sx={havcCancelButton}
          >
            Hayır, Vazgeç
          </Button>
          <Button
            variant="contained"
            endIcon={<CheckIcon />}
            onClick={() => {
              setReminder(false);
              if (checkOutdoorBarcodeInNonLgmvSystem()) {
                setLgmvWarning(true);
              } else {
                save();
              }
            }}
            sx={havcPrimaryButton}
          >
            Evet, Gönder
          </Button>
        </DialogActions>
      </Dialog>

      {/* LGMV Warning Dialog */}
      <Dialog
        open={lgmvWarning}
        onClose={() => setLgmvWarning(false)}
        sx={styles.reminderDialog}
        TransitionComponent={Fade}
        transitionDuration={300}
      >
        <DialogTitle sx={{
          ...styles.reminderTitle,
          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              LGMV Uyarısı
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ ...styles.reminderContent, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}>
          <Alert severity="warning" sx={{ textAlign: 'left', width: '100%' }}>
            LGMV dosyanız olmadığı için <strong>Dış Üniteler</strong> onaya gönderilmeyecektir.
            <br /><br />
            Sadece dış ünite harici ekipmanlar (iç üniteler, kumandalar vb.) merkeze gönderilecektir.
          </Alert>
        </DialogContent>
        <DialogActions sx={styles.reminderActions}>
          <Button
            variant="outlined"
            startIcon={<CloseIcon />}
            onClick={() => setLgmvWarning(false)}
            sx={havcCancelButton}
          >
            Vazgeç
          </Button>
          <Button
            variant="contained"
            endIcon={<CheckIcon />}
            onClick={() => {
              setLgmvWarning(false);
              save();
            }}
            sx={havcPrimaryButton}
          >
            Anladım, Devam Et
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Receipts;
